import { supabase } from '../../lib/supabase';

export interface BlueprintBasic {
  id: string;
  title: string;
  logo_url: string | null;
  description: string;
  download_url: string;
}

// Get all blueprints
export const fetchAllBlueprints = async (): Promise<BlueprintBasic[]> => {
  try {
    const { data, error } = await supabase
      .from('blueprints')
      .select('id, title, logo_url, description, download_url');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching blueprints:', error);
    throw error;
  }
};

// Get blueprints for a specific class
export const getClassBlueprints = async (classId: string): Promise<BlueprintBasic[]> => {
  try {
    const { data, error } = await supabase
      .from('class_blueprints')
      .select(`
        blueprint_id,
        blueprints:blueprint_id (id, title, logo_url, description, download_url)
      `)
      .eq('class_id', classId);
    
    if (error) throw error;
    
    // Transform data to return just the blueprint objects
    return (data || []).map(item => item.blueprints);
  } catch (error) {
    console.error('Error fetching class blueprints:', error);
    throw error;
  }
};

// Attach blueprints to a class
export const attachBlueprintsToClass = async (classId: string, blueprintIds: string[]): Promise<void> => {
  try {
    // First, remove all existing associations
    const { error: deleteError } = await supabase
      .from('class_blueprints')
      .delete()
      .eq('class_id', classId);
    
    if (deleteError) throw deleteError;
    
    // Skip if no blueprints to add
    if (blueprintIds.length === 0) return;
    
    // Create new associations
    const associations = blueprintIds.map(blueprintId => ({
      class_id: classId,
      blueprint_id: blueprintId
    }));
    
    const { error: insertError } = await supabase
      .from('class_blueprints')
      .insert(associations);
    
    if (insertError) throw insertError;
  } catch (error) {
    console.error('Error attaching blueprints to class:', error);
    throw error;
  }
};

// Update the ClassData interface to include blueprints
export const updateClassWithBlueprints = async (classId: string, blueprintIds: string[]): Promise<void> => {
  return attachBlueprintsToClass(classId, blueprintIds);
};
